import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, tzUsernameHeadersSchema, usernameHeadersSchema } from '../schemas';
import { issueSchema } from '@shared/interfaces/IIssueShared';
import { minimizedProfessionSchema } from '@shared/interfaces/IMinimizedProfession';
import { IssueCommentType } from '@shared/interfaces/ICommentShared';

// ============================================================
// Param Schemas
// ============================================================

const projectParamsSchema = z.object({
	projectId: requiredStringSchema,
});

const projectIssueParamsSchema = z.object({
	projectId: requiredStringSchema,
	issueId: requiredStringSchema,
});

const projectAssigneeParamsSchema = z.object({
	projectId: requiredStringSchema,
	username: requiredStringSchema,
});

// ============================================================
// Query Schemas
// ============================================================

const issuesQuerySchema = z.object({
	notCompleted: z.string().optional(),
	linkedToSequence: z.string().optional(),
	linkedToActivity: z.string().optional(),
	excludeInitial: z.string().optional(),
	isSafety: z.string().optional(),
	floorId: z.string().optional(),
	areaId: z.string().optional(),
	professionIds: z.array(z.string()).optional(),
	username: z.string().optional(),
});

const locationCountsQuerySchema = z.object({
	floorIds: z.array(z.string()).optional(),
	professionIds: z.array(z.string()).optional(),
});

// ============================================================
// Body Schemas
// ============================================================

const reassignIssuesBodySchema = z.object({
	assignments: z.record(z.string(), z.string()),
});

/**
 * The project is addressed by the URI, so it must not also travel in the body —
 * a second carrier is a second thing a handler could trust.
 */
/**
 * Exported so callers can type their input from the CONTRACT rather than keep a
 * parallel local interface. `ICreateIssue` in `shared/interfaces/IIssue.ts` is
 * one of those: it still carries `projectId`, which moved to the path when
 * Issues was migrated, so it describes a body this route no longer accepts.
 */
export const createIssueBodySchema = issueSchema
	.omit({ _id: true, projectId: true, createDate: true, assignee: true })
	.extend({
		// `profession` was overridden to the FULL `professionSchema` while both the
		// clients and the stored document hold a MINIMIZED one — `issueSchema`
		// itself types it as `minimizedProfessionSchema`, and the service inserts
		// the body unchanged. Requiring the full shape meant a caller holding a
		// minimized profession (which is all either client has) could not satisfy
		// the body. Inheriting from `issueSchema` keeps it minimized, and a caller
		// that happens to hold a full profession still passes — Zod strips the
		// extra keys, which is what the database ends up with anyway.
		//
		// `issueSchema.profession` is the DISPLAY shape (it has to be: it is also
		// the response schema, and the serializer deletes whatever the schema
		// omits). Pinning it back to minimized here keeps that widening on the
		// read path only — a create still stores the six fields it always stored,
		// rather than persisting an enriched profession whose colour would go
		// stale the moment the project's profession is recoloured.
		profession: minimizedProfessionSchema.optional(),
		assignee: z.object({ username: z.string() }).nullable().optional(),
	});

/**
 * `projectId` is absent by construction: an update that could set it would move
 * an issue between projects.
 */
export const updateIssueBodySchema = issueSchema
	.omit({ _id: true, projectId: true })
	// Same reason as the create body: the response shape must carry the enriched
	// profession, the stored shape must not.
	.extend({ profession: minimizedProfessionSchema.optional() })
	.partial();

export type ICreateIssueBody = z.infer<typeof createIssueBodySchema>;
export type IUpdateIssueBody = z.infer<typeof updateIssueBodySchema>;

const issueCommentBodySchema = z.object({
	name: requiredStringSchema,
	username: requiredStringSchema,
	value: requiredStringSchema,
});

const issueAttachmentBodySchema = z.object({
	name: requiredStringSchema,
	username: requiredStringSchema,
	type: z.enum([IssueCommentType.IMAGE, IssueCommentType.PDF]),
	file: z.file().max(10_000_000).mime(['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf']),
});

// ============================================================
// Response Schemas
// ============================================================

const resourceCountByLocationSchema = z.object({
	totalCount: z.number(),
	floors: z.record(
		z.string(),
		z.object({
			floorTotal: z.number(),
			areas: z.record(z.string(), z.number()),
		})
	),
});

const reassignIssuesResponseSchema = z.object({
	updated: z.number(),
});

// ============================================================
// Contract
// ============================================================

export const issuesContract = defineContract(
	'issues',
	{
		getIssues: route({
			method: 'GET',
			path: '/projects/:projectId/issues',
			params: projectParamsSchema,
			query: issuesQuerySchema,
			response: z.array(issueSchema),
			operationId: 'getIssues',
			tags: ['Issues'],
			summary: 'List the issues of a project',
		}),
		createIssue: route({
			method: 'POST',
			path: '/projects/:projectId/issues',
			params: projectParamsSchema,
			body: createIssueBodySchema,
			headers: tzUsernameHeadersSchema,
			response: issueSchema,
			operationId: 'createIssue',
			tags: ['Issues'],
			summary: 'Create an issue in a project',
		}),
		reassignIssues: route({
			method: 'PATCH',
			path: '/projects/:projectId/issues',
			params: projectParamsSchema,
			body: reassignIssuesBodySchema,
			headers: usernameHeadersSchema,
			response: reassignIssuesResponseSchema,
			operationId: 'reassignIssues',
			tags: ['Issues'],
			summary: 'Reassign the assignee of several issues of a project',
		}),
		getIssuesLocationCounts: route({
			method: 'GET',
			path: '/projects/:projectId/issues/locationCounts',
			params: projectParamsSchema,
			query: locationCountsQuerySchema,
			response: resourceCountByLocationSchema,
			operationId: 'getIssuesLocationCounts',
			tags: ['Issues'],
			summary: 'Get issue counts grouped by location',
		}),
		getIssueById: route({
			method: 'GET',
			path: '/projects/:projectId/issues/:issueId',
			params: projectIssueParamsSchema,
			response: issueSchema,
			operationId: 'getIssueById',
			tags: ['Issues'],
			summary: 'Get an issue by id',
		}),
		updateIssue: route({
			method: 'PUT',
			path: '/projects/:projectId/issues/:issueId',
			params: projectIssueParamsSchema,
			body: updateIssueBodySchema,
			headers: tzUsernameHeadersSchema,
			response: issueSchema,
			operationId: 'updateIssue',
			tags: ['Issues'],
			summary: 'Update an issue',
		}),
		deleteIssue: route({
			method: 'DELETE',
			path: '/projects/:projectId/issues/:issueId',
			params: projectIssueParamsSchema,
			response: z.string(),
			operationId: 'deleteIssue',
			tags: ['Issues'],
			summary: 'Delete an issue',
		}),
		addIssueComment: route({
			method: 'POST',
			path: '/projects/:projectId/issues/:issueId/comments',
			params: projectIssueParamsSchema,
			body: issueCommentBodySchema,
			response: issueSchema,
			operationId: 'addIssueComment',
			tags: ['Issues'],
			summary: 'Add a text comment to an issue',
		}),
		/**
		 * Separate from `comments` because the payload is multipart: a route declared
		 * `multipart/form-data` parses every request as form data, so one endpoint
		 * cannot serve both JSON text comments and uploads.
		 */
		addIssueAttachment: route({
			method: 'POST',
			path: '/projects/:projectId/issues/:issueId/attachments',
			contentType: 'multipart/form-data',
			params: projectIssueParamsSchema,
			body: issueAttachmentBodySchema,
			response: issueSchema,
			operationId: 'addIssueAttachment',
			tags: ['Issues'],
			summary: 'Add a file attachment comment to an issue',
		}),
		getIssuesByAssignee: route({
			method: 'GET',
			path: '/projects/:projectId/users/:username/issues',
			params: projectAssigneeParamsSchema,
			response: z.array(issueSchema),
			operationId: 'getIssuesByAssignee',
			tags: ['Issues'],
			summary: 'List the issues of a project assigned to a user',
		}),
	},
	{ root: true }
);
